/**
 * Pure reference-counting subscription manager.
 *
 * Tracks which subscribers are interested in which symbols.
 * Calls onFirstSubscriber / onLastUnsubscriber callbacks so the
 * owner (marketdata.jsx) can wire them to SignalR register/unregister.
 *
 * Zero external dependencies — fully unit-testable.
 */
export class QuoteSubscriptionManager {
  constructor(options = {}) {
    /** @type {Map<string, Map<string, string>>} canonical → Map<subscriberId, originalSymbol> */
    this.symbolSubscribers = new Map();

    /** Called with (canonicalSymbol) when the first subscriber arrives */
    this.onFirstSubscriber = options.onFirstSubscriber || (() => {});

    /** Called with (canonicalSymbol) when the last subscriber leaves */
    this.onLastUnsubscriber = options.onLastUnsubscriber || (() => {});

    /** Maps any symbol to its canonical form (e.g. WING26 → WINFUT) */
    this.getCanonicalSymbol = options.getCanonicalSymbol || ((s) => s);
  }

  /**
   * Register a subscriber for a symbol.
   * @param {string} symbol - raw symbol (may be non-canonical)
   * @param {string} subscriberId - unique subscriber key
   */
  register(symbol, subscriberId = "legacy") {
    const canonical = this.getCanonicalSymbol(symbol);

    if (!this.symbolSubscribers.has(canonical)) {
      this.symbolSubscribers.set(canonical, new Map());
    }

    const subscribers = this.symbolSubscribers.get(canonical);
    const isFirst = subscribers.size === 0;

    subscribers.set(subscriberId, symbol);

    if (isFirst) {
      this.onFirstSubscriber(canonical);
    }
  }

  /**
   * Unregister a subscriber from a symbol.
   * @param {string} symbol - raw symbol
   * @param {string} subscriberId - must match the register call
   */
  unregister(symbol, subscriberId = "legacy") {
    const canonical = this.getCanonicalSymbol(symbol);

    if (!this.symbolSubscribers.has(canonical)) return;

    const subscribers = this.symbolSubscribers.get(canonical);
    subscribers.delete(subscriberId);

    if (subscribers.size === 0) {
      this.symbolSubscribers.delete(canonical);
      this.onLastUnsubscriber(canonical);
    }
  }

  /**
   * Check if a symbol has any active subscribers.
   * @param {string} symbol
   * @returns {boolean}
   */
  hasSubscribers(symbol) {
    const canonical = this.getCanonicalSymbol(symbol);
    const subs = this.symbolSubscribers.get(canonical);
    return subs != null && subs.size > 0;
  }

  /**
   * Return a snapshot of all subscriptions for debugging / assertions.
   * @returns {Array<{symbol: string, refCount: number, subscribers: string}>}
   */
  debug() {
    const data = [];
    this.symbolSubscribers.forEach((subscribers, symbol) => {
      data.push({
        symbol,
        refCount: subscribers.size,
        subscribers: Array.from(subscribers.keys()).join(", "),
      });
    });
    return data;
  }

  /**
   * Remove all subscriptions (does NOT fire onLastUnsubscriber).
   */
  clear() {
    this.symbolSubscribers.clear();
  }
}
